import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, Heart, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";

export default function Header() {
  const { count, setIsOpen, shakeTick } = useCart();
  const { count: wishCount } = useWishlist();
  const { user } = useAuth();
  const [shake, setShake] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (shakeTick === 0) return;
    setShake(true);
    const t = setTimeout(() => setShake(false), 550);
    return () => clearTimeout(t);
  }, [shakeTick]);

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${
      isActive ? "text-[color:var(--brand-primary)]" : "text-[color:var(--brand-neutral)] hover:text-[color:var(--brand-primary)]"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[color:var(--brand-bg)]/85 border-b border-[color:var(--brand-border)]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
          <img src="/brand/logo.png" alt="Mimes Gourmet" className="h-10 sm:h-12 w-auto" />
          <span className="eyebrow hidden sm:inline text-[10px] border-l border-[color:var(--brand-border)] pl-3 text-[color:var(--brand-neutral)]">Artesanas · Graus, Pirineo</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" data-testid="nav-home" className={linkClass} end>
            Inicio
          </NavLink>
          <NavLink to="/tienda" data-testid="nav-shop" className={linkClass}>
            Tienda
          </NavLink>
          <NavLink to="/blog" data-testid="nav-blog" className={linkClass}>
            Blog
          </NavLink>
          <NavLink to="/historia" data-testid="nav-story" className={linkClass}>
            Historia
          </NavLink>
          <NavLink to="/contacto" data-testid="nav-contact" className={linkClass}>
            Contacto
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <NavLink
            to={user && user.role === "customer" ? "/cuenta" : "/cuenta/entrar"}
            data-testid="account-link"
            className="p-2 rounded-md hover:bg-[color:var(--brand-bg-alt)] transition-colors hidden sm:inline-flex"
            aria-label="Mi cuenta"
          >
            <User className="w-5 h-5 text-[color:var(--brand-primary)]" strokeWidth={1.5} />
          </NavLink>
          <NavLink to="/favoritos" data-testid="wishlist-link" className="relative p-2 rounded-md hover:bg-[color:var(--brand-bg-alt)] transition-colors hidden sm:inline-flex" aria-label="Favoritos">
            <Heart className="w-5 h-5 text-[color:var(--brand-primary)]" strokeWidth={1.5} />
            {wishCount > 0 && (
              <span data-testid="wishlist-count-badge" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 flex items-center justify-center text-[10px] font-semibold text-white bg-[color:var(--brand-sage)] rounded-full">{wishCount}</span>
            )}
          </NavLink>
          <button
            data-testid="cart-button"
            onClick={() => setIsOpen(true)}
            className="relative p-2 rounded-md hover:bg-[color:var(--brand-bg-alt)] transition-colors"
            aria-label="Abrir carrito"
          >
            <ShoppingBag
              className={`w-5 h-5 text-[color:var(--brand-primary)] ${shake ? "animate-cart-shake" : ""}`}
              strokeWidth={1.5}
            />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 min-w-5 h-5 px-1 flex items-center justify-center text-[10px] font-semibold text-white bg-[color:var(--brand-terracotta)] rounded-full"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="md:hidden p-2 rounded-md hover:bg-[color:var(--brand-bg-alt)]"
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menú"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[color:var(--brand-border)] bg-[color:var(--brand-bg)] px-5 py-4 flex flex-col gap-3">
          <NavLink to="/" onClick={() => setMobileOpen(false)} className={linkClass} end>
            Inicio
          </NavLink>
          <NavLink to="/tienda" onClick={() => setMobileOpen(false)} className={linkClass}>
            Tienda
          </NavLink>
          <NavLink to="/blog" onClick={() => setMobileOpen(false)} className={linkClass}>
            Blog
          </NavLink>
          <NavLink to="/historia" onClick={() => setMobileOpen(false)} className={linkClass}>
            Historia
          </NavLink>
          <NavLink to="/contacto" onClick={() => setMobileOpen(false)} className={linkClass}>
            Contacto
          </NavLink>
        </div>
      )}
    </header>
  );
}
